/**
 * Arabic-aware text normalisation.
 *
 * Two features need this and must agree exactly, or they contradict each other:
 *
 * - **FEAT-011** — company names are unique *case- and diacritic-insensitively*.
 *   `citext` handles case but not diacritics, and Arabic names differ far more
 *   often by hamza seat or taa marbuta than by case. Two spellings of one name
 *   are the same company to everyone in the building, and registering both is a
 *   data-quality failure that is very hard to undo later.
 * - **FEAT-015 / BR-042** — search applies the same normalisation to the query
 *   *and* to the indexed content. If the two used different rules, a company
 *   would be findable under a spelling it could not be registered under.
 *
 * So the rules live here once, and both features import them.
 *
 * Every character class below is written as escape sequences rather than as
 * literal characters. Most of them are invisible — combining marks, zero-width
 * joiners, bidi controls — and a reviewer cannot check a rule they cannot see.
 *
 * What this deliberately does **not** do is transliterate or stem. It only
 * collapses forms that Arabic readers treat as the same character.
 */

/** Tashkeel, the combining hamza and maddah marks, superscript alef, and
 *  the tatweel elongation mark. The range must reach U+0670: NFKD splits
 *  the hamza forms of alef into a bare alef plus a combining mark, and if
 *  that mark survives, the alef forms never collapse. */
const DIACRITICS = /[\u064B-\u0670\u0640]/g;

/** Zero-width joiners and bidi controls: invisible, but they break comparison. */
const ZERO_WIDTH = /[\u200B-\u200F\u202A-\u202E\uFEFF]/g;

/** Alef madda, hamza above, hamza below, wasla → bare alef. */
const ALEF_FORMS = /[آأإٱ]/g;
const BARE_ALEF = 'ا';

/** Taa marbuta → haa. The distinction is not reliably typed. */
const TAA_MARBUTA = /ة/g;
const HAA = 'ه';

/** Alef maqsura → yaa, for the same reason. */
const ALEF_MAQSURA = /ى/g;
const YAA = 'ي';

/** Waw and yaa hamza carriers → the bare hamza. */
const HAMZA_CARRIERS = /[ؤئ]/g;
const BARE_HAMZA = 'ء';

const ARABIC_INDIC_ZERO = 0x0660;
const EASTERN_ARABIC_INDIC_ZERO = 0x06f0;

/**
 * Maps Arabic-Indic and Eastern Arabic-Indic digits onto Western ones.
 *
 * BR-042 requires an Arabic-typed phone number to match the same number typed in
 * Latin digits — someone using an Arabic keyboard should still find the record.
 */
export function normaliseDigits(input: string): string {
  let out = '';
  for (const character of input) {
    const code = character.codePointAt(0)!;

    if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) {
      out += String(code - ARABIC_INDIC_ZERO);
    } else if (code >= EASTERN_ARABIC_INDIC_ZERO && code <= EASTERN_ARABIC_INDIC_ZERO + 9) {
      out += String(code - EASTERN_ARABIC_INDIC_ZERO);
    } else {
      out += character;
    }
  }
  return out;
}

/** Digits only, Arabic-Indic normalised first. Used for phone matching (BR-066). */
export function digitsOnly(input: string): string {
  return normaliseDigits(input).replace(/\D/g, '');
}

/**
 * The canonical comparison form of a piece of text.
 *
 * Order matters: decomposing first (NFKD) separates combining marks from their
 * base letters so the diacritic strip can remove them, and the letter-form
 * collapses then run on the bare consonants.
 */
export function normaliseText(input: string): string {
  return normaliseDigits(input)
    .normalize('NFKD')
    .replace(DIACRITICS, '')
    .replace(ZERO_WIDTH, '')
    .replace(ALEF_FORMS, BARE_ALEF)
    .replace(TAA_MARBUTA, HAA)
    .replace(ALEF_MAQSURA, YAA)
    .replace(HAMZA_CARRIERS, BARE_HAMZA)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The value stored in a `*_normalised` column and compared for uniqueness.
 *
 * Identical to `normaliseText` today. It exists as a separate name because the
 * two have different reasons to change: loosening *search* matching is a product
 * decision, while loosening *uniqueness* would let two companies that already
 * exist suddenly collide. Keeping the names distinct means that divergence, if
 * it ever comes, is a deliberate edit rather than a silent side effect.
 */
export function normaliseForUniqueness(input: string): string {
  return normaliseText(input);
}
